import { BASEURL } from '@services/network';
import { logError } from '@utils/usefulFuncs';
import { logError as logSentryError } from '@utils/sentry';
import { createAuthedEventSource } from '@utils/streamAuth';
import React from 'react';

const getStreamStatusUrl = (runId, { includeContext, includeAgentPlan }) => {
  const params = [`run_id=${encodeURIComponent(runId)}`];
  if (includeContext) params.push('include_context=true');
  if (includeAgentPlan) params.push('include_agent_plan=true');
  return `${BASEURL}/api/v2/genai/queries/complete_event?${params.join('&')}`;
};

export const useStreamStatus = (runId, options = {}) => {
  const [streamStatus, setStreamStatus] = React.useState({});
  const [streamErrors, setStreamErrors] = React.useState({});
  const { headers, includeContext = false, includeAgentPlan = false } = options;

  React.useEffect(() => {
    let source = null;

    const openStreamStatus = async (runId) => {
      const URL = getStreamStatusUrl(runId, { includeContext, includeAgentPlan });
      source = createAuthedEventSource(URL, {
        headers,
        heartbeatTimeout: 600000,
      });
      source.addEventListener('complete', function (event) {
        try {
          setStreamStatus((prevState) => ({ ...prevState, [runId]: true }));
          source.close();
        } catch (e) {
          logError('error occurred in stream', e);
          source.close();
        }
      });
      source.addEventListener('error', function (error) {
        try {
          setStreamErrors((prevState) => ({ ...prevState, [runId]: true }));

          logSentryError(new Error('Error streaming answer'), {
            error: error?.toString(),
            message: error?.message,
            component: 'useStreamStatus',
          });

          source.close();
        } catch (e) {
          logError('error occurred in stream', e);
          source.close();
        }
      });
    };

    if (runId) {
      openStreamStatus(runId);
    }

    return () => {
      if (source) {
        source.close();
      }
    };
  }, [runId, headers, includeContext, includeAgentPlan]);

  return React.useMemo(
    () => (streamErrors[runId] ? 'error' : streamStatus[runId] ? 'complete' : 'pending'),
    [runId, streamErrors, streamStatus]
  );
};
